import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/libs/utils";
import { ItemSheetObject } from "@/types/itemsheet";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { parseItemSheetObjectToRows } from "../../Estimate/utils";
import { createColumns } from "../columns/estimateColumns";
import { useMemo } from "react";
import { useReadProjectGroupsItemSheetLatest } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useEstimateInfo } from "../hooks/useScenarioStore";
import { useParams } from "react-router-dom";
import { DEFAULT_PAGINATION } from "@/consts/common";

type Props = {
  itemSheetId: string;
};

export default function ScenarioEstimateTable({ itemSheetId }: Props) {
  const { group_id = "" } = useParams();

  const estimateInfo = useEstimateInfo();

  const { data: sheetData } = useReadProjectGroupsItemSheetLatest(
    group_id,
    itemSheetId,
    {
      project_id: estimateInfo.projectId,
      project_type: estimateInfo.projectType,
      original_id: estimateInfo.originalId,
    }
  );

  const fields = useMemo(() => {
    if (!sheetData?.latest?.data) return [];

    const { data } = sheetData?.latest;

    return parseItemSheetObjectToRows(data as ItemSheetObject);
  }, [sheetData]);
  const columns = useMemo(() => createColumns(), []);

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: DEFAULT_PAGINATION.limit,
      },
    },
  });

  return (
    <div className="w-full h-full overflow-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="px-2 text-sm text-white bg-dark-red-60 first:rounded-tl-md last:rounded-tr-md"
                  style={{
                    minWidth: header.column.columnDef.size,
                    maxWidth: header.column.columnDef.size,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  row.index % 2 === 0 ? "bg-white" : "bg-light-gray"
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.size,
                    }}
                    className="min-h-12 py-[10px] px-2"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                검색 결과가 없습니다.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
